import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Typography, Tooltip } from 'antd'
import { useNavigate } from 'react-router-dom'
import { StarOutlined, StarFilled } from '@ant-design/icons'

import useAcesso from 'Hooks/acesso/useAcesso'

import './SubmenuPanel.less'

const { Text } = Typography

const MenuItemContent = ({ label, onStarClick, isFavorite }) => {
  const textRef = useRef(null)
  const [isTextTruncated, setIsTextTruncated] = useState(false)

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isTruncated =
          textRef.current.scrollWidth > textRef.current.clientWidth
        setIsTextTruncated(isTruncated)
      }
    }

    // Initial check
    checkTruncation()

    // Create ResizeObserver
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to prevent ResizeObserver loop
      window.requestAnimationFrame(checkTruncation)
    })

    if (textRef.current) {
      resizeObserver.observe(textRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [label])

  const textElement = (
    <Text ref={textRef} ellipsis style={{ maxWidth: 'calc(100% - 40px)' }}>
      {label}
    </Text>
  )

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%'
      }}
    >
      {isTextTruncated ? (
        <Tooltip title={label} placement="topLeft">
          {textElement}
        </Tooltip>
      ) : (
        textElement
      )}
      <Button
        type="text"
        size="small"
        icon={
          isFavorite ? (
            <StarFilled style={{ color: '#FFD700' }} />
          ) : (
            <StarOutlined />
          )
        }
        onClick={onStarClick}
        style={{
          border: 'none',
          padding: '4px',
          marginLeft: '8px',
          flexShrink: 0
        }}
      />
    </div>
  )
}

const submenuItemShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  path: PropTypes.string,
  permission: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number)
  ]),
  subitems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
      permission: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.number)
      ])
    })
  )
})

const SubmenuPanel = ({
  visible,
  submenus,
  onClose,
  favorites,
  onFavoriteChange,
  triggerElement,
  setCollapsed
}) => {
  const navigate = useNavigate()
  const panelRef = useRef(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const usuario =
    localStorage.getItem('earetaguarda_usuario_login') || 'defaultUser'
  const { checkControleAcesso } = useAcesso()

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && visible) {
        onClose()
      }
    }

    const handleClickOutside = (event) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        triggerElement &&
        !triggerElement.contains(event.target)
      ) {
        onClose()
      }
    }

    if (visible && triggerElement) {
      const rect = triggerElement.getBoundingClientRect()
      const isCollapsed = document.querySelector('.ant-layout-sider-collapsed')
      const leftOffset = isCollapsed ? 80 : 300

      // Calculate if we need to grow upwards
      const panelHeight = panelRef.current?.offsetHeight || 0
      const windowHeight = window.innerHeight
      const spaceBelow = windowHeight - rect.top

      // If there's not enough space below, position above the trigger
      const top =
        spaceBelow < panelHeight + 20 ? rect.bottom - panelHeight : rect.top

      // Ensure the panel stays within viewport bounds
      const maxTop = windowHeight - panelHeight - 20
      const finalTop = Math.min(Math.max(top, 20), maxTop)

      setPosition({
        top: finalTop,
        left: leftOffset
      })
    }

    document.addEventListener('keydown', handleEscKey)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('keydown', handleEscKey)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [visible, onClose, triggerElement])

  const handleFavorite = (item, parentTitle) => {
    const favoriteItem = {
      ...item,
      parentLabel: parentTitle
    }

    const isAlreadyFavorited = favorites.some((fav) => fav.key === item.key)

    let updatedFavorites
    if (isAlreadyFavorited) {
      updatedFavorites = favorites.filter((fav) => fav.key !== item.key)
    } else {
      updatedFavorites = [...favorites, favoriteItem].sort((a, b) => {
        if (a.parentLabel && b.parentLabel && a.parentLabel !== b.parentLabel) {
          return a.parentLabel.localeCompare(b.parentLabel)
        }

        if (a.parentLabel === b.parentLabel) {
          return a.label.localeCompare(b.label)
        }
        return 0
      })
    }

    onFavoriteChange(updatedFavorites)
    localStorage.setItem(
      `favorites_${usuario}`,
      JSON.stringify(updatedFavorites)
    )
  }

  const handleItemClick = (item) => {
    navigate(item.path)
    onClose()
    // Collapse the sidebar when navigating
    if (setCollapsed) {
      setCollapsed(true)
    }
  }

  if (!visible || !submenus) return null

  const renderItems = (items, parentTitle) => {
    if (!items) return null

    return items.map((item) => {
      if (item.subitems) {
        const itemCount = item.subitems.length
        const itemsAttribute = itemCount > 10 ? 'many' : 'few'

        return (
          <div
            key={item.key}
            className="app__submenu-panel__group"
            data-items={itemsAttribute}
          >
            <h4 className="app__submenu-panel__group-title">{item.label}</h4>
            <div className="app__submenu-panel__items">
              {renderItems(item.subitems, item.label)}
            </div>
          </div>
        )
      }

      const isFavorite = favorites.some((fav) => fav.key === item.key)
      const hasPermission = item.permission
        ? checkControleAcesso(item.permission)
        : true

      return (
        <div
          key={item.key}
          className={`app__submenu-panel__item ${
            !hasPermission ? 'app__submenu-panel__item--disabled' : ''
          }`}
          onClick={() => hasPermission && handleItemClick(item)}
          onKeyDown={(e) => {
            if (hasPermission && (e.key === 'Enter' || e.key === ' ')) {
              handleItemClick(item)
            }
          }}
          role="button"
          tabIndex={hasPermission ? 0 : -1}
          style={{
            opacity: hasPermission ? 1 : 0.5,
            cursor: hasPermission ? 'pointer' : 'not-allowed'
          }}
        >
          <MenuItemContent
            label={item.label}
            isFavorite={isFavorite}
            onStarClick={(e) => {
              e.stopPropagation()
              handleFavorite(item, parentTitle)
            }}
          />
        </div>
      )
    })
  }

  return (
    <div
      ref={panelRef}
      className="app__submenu-panel"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      <div className="app__submenu-panel__content">
        {submenus.map((submenu) => (
          <div key={submenu.title} className="app__submenu-panel__section">
            <h3 className="app__submenu-panel__title">{submenu.title}</h3>
            <div className="app__submenu-panel__items">
              {renderItems(submenu.items, submenu.title)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

MenuItemContent.propTypes = {
  label: PropTypes.string.isRequired,
  onStarClick: PropTypes.func.isRequired,
  isFavorite: PropTypes.bool.isRequired
}

SubmenuPanel.propTypes = {
  visible: PropTypes.bool.isRequired,
  submenus: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(submenuItemShape).isRequired
    })
  ),
  onClose: PropTypes.func.isRequired,
  favorites: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      parentLabel: PropTypes.string
    })
  ).isRequired,
  onFavoriteChange: PropTypes.func.isRequired,
  triggerElement: PropTypes.instanceOf(Element),
  setCollapsed: PropTypes.func
}

SubmenuPanel.defaultProps = {
  submenus: [],
  triggerElement: null,
  setCollapsed: null
}

export default SubmenuPanel
